//
// Copyright (c) 2025 Comcast
//

import { useEffect, useRef, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { WikiMarkupTransformer } from '@atlaskit/editor-wikimarkup-transformer'
import { EditorContent, useEditor } from '@tiptap/react'
import { history } from '@tiptap/pm/history'
import { Placeholder } from '@tiptap/extensions'
import StarterKit from '@tiptap/starter-kit'
import { TableKit } from '@tiptap/extension-table'
// StarterKit already bundles Bold / Italic / HorizontalRule, but we disable them in StarterKit.configure()
// and import them separately to customize (rename via extend). Because we import them directly, they must be
// explicitly listed in package.json.
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import HorizontalRule from '@tiptap/extension-horizontal-rule'

import DynamicTextArea from 'components/Shared/DynamicTextArea/Index'
import Toolbar from './Toolbar/Index'
import TextColor from './extensions/TextColor'
import SubSup from './extensions/SubSup'

import colors from 'components/utils/colors'
import { hasAdfJsonUnsupportedTypes } from './utils'

import '@tiptap-ui/styles/_variables.scss'
import '@tiptap-ui/styles/_keyframe-animations.scss'

const LINE_VERT_PADDINGS = 18
const LINE_HEIGHT = 20

const StyledEditorContent = styled(EditorContent)`
  background: #FFFFFF;
  color: ${colors.contentSecondaryDefault};
  line-height: ${LINE_HEIGHT}px;

  .tiptap {
    font-size: 14px;
    padding: 8px;
    outline: none;
    border-radius: 3px;
    border: 1px solid ${colors.secondary70};
    min-height: 32px;
    word-break: break-word;
    max-height: 600px;
    overflow-y: auto;

    ${props => props.$minRows && css`
      min-height: ${LINE_VERT_PADDINGS + props.$minRows * LINE_HEIGHT}px;
    `}

    ${props => props.$isReadOnly && css`
      max-height: unset;
      overflow-y: visible;
    `}

    &:focus {
      border-color: ${colors.primary40};
    }

    /* placeholder */
    p.is-editor-empty:first-child::before {
      color: ${colors.secondary70};
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }

    /* styles for editor HTML elements */
    strong {
      font-weight: bold;
    }


    h1, h2, h3, h4, h5, h6 {
      font-family: Exo;
    }
    h1 {
      font-size: 40px;
      font-weight: 300;
      line-height: 52px;
    }
    h2 {
      font-size: 28px;
      font-weight: 300;
      line-height: 37px;
    }
    h3 {
      font-size: 20px;
      font-weight: 400;
      line-height: 28px;
    }
    h4 {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
    }
    h5 {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
    }
    h6 {
      font-size: 12px;
      font-weight: 500;
      line-height: 18px;
    }

    a {
      cursor: ${props => props.$isReadOnly ? 'pointer' : 'text'};
    }

    em {
      font-style: italic;
    }

    table {
      margin-top: 8px;
      margin-bottom: 16px;

      .selectedCell {
        background: ${colors.primary90};
      }
    }
    th {
      font-family: Exo;
      font-weight: 600;
      font-size: 10px;
      line-height: 14px;
      letter-spacing: 0.3px;
      color: ${colors.contentSecondaryTitle};
      border: 1px solid ${colors.borderSecondaryDefault};
      padding: 4px 16px;
    }
    td {
      border: 1px solid ${colors.borderSecondaryDefault};
      padding: 12px 16px;
    }

    ul {
      padding-inline-start: 24px;
      list-style: disc;
      li {
        text-indent: -4px;
      }
    }
    ol {
      padding-inline-start: 20px;
      list-style: decimal;
      li {
        text-indent: 2px;
      }
    }

    sub, sup {
      font-size: smaller;
    }
    sub {
      vertical-align: sub;
    }
    sup {
      vertical-align: super;
    }

    blockquote {
      border-left: 1px solid ${colors.borderSecondaryDefault};
      color: ${colors.contentSecondaryInactive};
      padding: 0 0 0 8px;
      margin: 8px 0 24px 0;
      font-weight: 600;
    }

    code, pre:has(code) {
      font-family: monospace;
      background: ${colors.bgSecondaryStripe};
    }
    pre:has(code) {
      padding: 16px;
      margin-bottom: 16px;
    }

    hr {
      border-color: ${colors.borderSecondaryDefault};
      border-top: none;
      border-left: none;
      border-right: none;
    }
  }

  ${props => props.$isReadOnly && css`
    background: transparent;
    color: ${colors.secondary20};
    .tiptap {
      padding: 0;
      border: none;
    }
  `}
`

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${p => p.width || '100%'};
  box-sizing: border-box;
`

const Strong = Bold.extend({ name: 'strong' })
const Em = Italic.extend({ name: 'em' })
const Rule = HorizontalRule.extend({ name: 'rule' })

const getExtensions = (placeholder) => ([
  StarterKit.configure({
    bold: false,
    italic: false,
    horizontalRule: false,
    link: {
      HTMLAttributes: {
        rel: 'noopener noreferrer',
      },
      openOnClick: false,
    },
  }),
  Placeholder.configure({ placeholder: placeholder }),
  TableKit,
  Strong,
  Em,
  Rule,
  TextColor,
  SubSup,
])

const MODE_TEXT = 'text'
const MODE_VISUAL = 'visual'

// top-level simple helper to detect unsupported ADF types for given wiki markup
const detectUnsupported = (wiki, editorInstance, transformer) => {
  if (wiki === '') {
    return false
  }
  const adfJsonFull = transformer.parse(wiki).toJSON()
  return hasAdfJsonUnsupportedTypes(adfJsonFull, editorInstance.schema)
}

const RichTextEditor = ({
  value = '', onChange, isReadOnly, width, placeholder, autoFocus, minRows,
}) => {
  const lastWiki = useRef('')
  const lastReadOnly = useRef(isReadOnly)
  // editor mode state
  const [mode, setMode] = useState(MODE_VISUAL)

  // Transformer with full ADF schema, including types, unsupported by TipTap.
  // It is used to detect the presence of such unsupported types in the wiki markup.
  const fullTransformer = useMemo(() => new WikiMarkupTransformer(), [])

  // on update in visual mode, encode ADF json back to jira wiki format
  const onUpdate = useCallback(({ editor }) => {
    if (mode !== MODE_VISUAL || isReadOnly) {
      return
    }

    const wiki = fullTransformer.encode(editor.state.doc)

    if (wiki === lastWiki.current) {
      return
    }

    lastWiki.current = wiki
    onChange?.(wiki)
  }, [onChange, fullTransformer, mode, isReadOnly])

  // content parameter is not set, it will be set in setContent()
  const editor = useEditor({
    extensions: getExtensions(placeholder),
    editable: !isReadOnly && mode === MODE_VISUAL,
    onUpdate: onUpdate,
    editorProps: {
      handleDOMEvents: {
        // completely disable dragging in the editor. In case if it is required,
        // it should be at least disabled from selected table cells because it corrupts tables:
        //   dragstart: (view, event) => {
        //     if (view.state.selection.constructor?.name === '_CellSelection') {
        //       event.preventDefault()
        //       return true
        //     }
        //     return false
        //   }
        dragstart: (view, event) => {
          event.preventDefault()
          return true
        },
      },
    },
    enableInputRules: false,
    enablePasteRules: false,
    autofocus: !!autoFocus,
  })

  // Update editor content and mode when external value changes
  useEffect(() => {
    if (!editor) {
      return
    }

    const isNewValue = value !== lastWiki.current
    const isNewReadOnly = isReadOnly !== lastReadOnly.current
    lastReadOnly.current = isReadOnly

    const isTransitionFromEmptyToNonEmpty = lastWiki.current === '' && value !== ''
    const isExitingReadOnlyWithContent = isNewReadOnly && !isReadOnly && value !== ''
    const shouldCheckSupport = isTransitionFromEmptyToNonEmpty || isExitingReadOnlyWithContent

    // set new mode (visual/text) detecting unsupported types
    let newMode = mode
    if (isReadOnly || value === '') {
      newMode = MODE_VISUAL
    } else if (shouldCheckSupport) {
      newMode = detectUnsupported(value, editor, fullTransformer) ? MODE_TEXT : MODE_VISUAL
    }
    if (newMode !== mode) {
      setMode(newMode)
      // set focus after switching from text edit component
      if (newMode === MODE_VISUAL) {
        setTimeout(() => editor.commands.focus(), 0)
      }
    }

    const isEditable = !isReadOnly && newMode === MODE_VISUAL
    if (isEditable !== editor.isEditable) {
      editor.setEditable(isEditable, false)
    }

    // update content if value changed externally or readonly mode was changed
    if (newMode === MODE_VISUAL && (isNewValue || isNewReadOnly)) {
      const safeTransformer = new WikiMarkupTransformer(editor.schema)
      const safeJson = safeTransformer.parse(value).toJSON()
      editor.commands.setContent(safeJson, { emitUpdate: false })

      // unregister and re-register history plugin to remove initial 'Undo'
      editor.unregisterPlugin('history')
      editor.registerPlugin(history())
    }
    lastWiki.current = value
  }, [value, isReadOnly, editor, fullTransformer, mode])

  const showTipTap = isReadOnly || mode === MODE_VISUAL
  const showToolbar = !isReadOnly && mode === MODE_VISUAL

  return (
    <StyledWrapper width={width} className='rte-scope'>
      {showToolbar && <Toolbar editor={editor} />}
      {showTipTap
        ? <StyledEditorContent editor={editor} $isReadOnly={isReadOnly} $minRows={minRows} />
        : (
          <DynamicTextArea
            value={value}
            onChange={e => {
              const v = e.target.value
              onChange?.(v)
            }}
            width={width}
            placeholder={placeholder}
            autoFocus={autoFocus}
            minRows={minRows}
          />
          )
      }
    </StyledWrapper>
  )
}

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  isReadOnly: PropTypes.bool,
  width: PropTypes.string,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  minRows: PropTypes.number,
}

export default RichTextEditor
